/**
 * Het antwoordvak in zijn vijf standen (mail, chat en WhatsApp,
 * Beslissen, Terugbellen, Live) plus alleen-lezen na afronden. Elke
 * stand heeft dezelfde opbouw: kopregel, inhoud, voet met de oranje
 * hoofdknop rechts. Het scherm verstuurt alleen via de bestaande
 * versturen-, per-mail-, terugbel- en overnamepaden.
 *
 * Sessie 70 (punt 13): bovenaan elke stand (behalve Live en Afgerond)
 * staat de kanaalkeuze. Het gekozen kanaal bepaalt welke stand open
 * staat; standaard is dat het kanaal van het laatste klantbericht, bij
 * Beslissen het kanaal van het voorstel.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { isToetsWeigering } from "@/lib/toetsWeigering";
import { plaatshouderMelding } from "@/lib/werkplek-api";

import {
  KANAAL_WOORD,
  KANALEN,
  v6Api,
  type Kanaal,
  type TegelConcept,
  type TegelVerstuurd,
  type TerugbelUitkomst,
  type V6Kaart,
  type V6Zaak,
} from "../api";
import { useV6 } from "../context";
import { Icoon } from "../Icoon";
import { lijstQuery } from "../lijstQuery";
import { meetNaAanvraag } from "../meting";
import {
  WACHT_OP_KLANT,
  naAfronden,
  naTerugzetten,
  planStilleVerversing,
  verversZaak,
  werkKaartBij,
  zetZaak,
} from "../naActie";
import { v6Sleutels } from "../sleutels";
import { voegVerzendingToe, zetVerzending } from "../verzendingen";
import type { ToonMelding } from "./index";
import { klokje } from "./tijd";
import { KlantKoppelVlak, type Gekoppeld } from "../KlantKoppelVlak";
import {
  BijlageChips,
  ConceptRegel,
  Hoofdknop,
  Kopregel,
  Tekstvak,
  VakFout,
  Voet,
  foutTekst,
  useBijlagen,
  useTekst,
  useVerzenden,
  type BijlageModus,
  type Moment,
} from "./vak";
import { KanaalKeuze } from "./kanaalkeuze";
import { htmlNaarTekst, tekstVoorControle } from "./mailopmaak";
import { MailVelden, useMailVelden, type MailVeldenStand } from "./MailVelden";
import { Werkbalk } from "./werkbalk";
import { TemplateKeuze, useTemplateKeuze } from "./templateKeuze";
import { vakConcept } from "./verstuurd";
import { Balk } from "./Balk";
import { Handtekening, Namens, useStem } from "./Handtekening";
import { middenSleutels, type M7Doorgezet } from "./middenApi";
import { useMidden } from "./middenQuery";
import { lijstZinnen } from "./Overdracht";
import "./beslis.css";

interface StandProps {
  zaak: V6Zaak;
  concept: TegelConcept;
  toonMelding: ToonMelding;
  /** De kanaalkeuze, direct onder de kopregel (sessie 70, punt 13). */
  keuze?: ReactNode;
  /** Wat bij een kanaalwissel meegaat naar de volgende stand. */
  bewaar?: Bewaarder;
}

/**
 * Recensie punt 13: bij een kanaalwissel wordt de stand opnieuw gemaakt.
 * De tekst gaat mee via de conceptlezer; wat JP aan Aan, CC, BCC en het
 * onderwerp veranderde gaat hierlangs mee, en het aantal bijlagen, zodat
 * het vak kan zeggen dat die niet meegaan.
 */
export interface VakBewaard {
  mail: { aan: string[]; cc: string[]; bcc: string[]; onderwerp: string } | null;
  bijlagen: number;
}

interface Bewaarder {
  lees: () => VakBewaard;
  zet: (deel: Partial<VakBewaard>) => void;
}

/** Staat dit kanaal uit? Dan de zin waarom, en dat er niets heen gaat. */
function kanaalUit(zaak: V6Zaak, kanaal: Kanaal): string | null {
  const stand = kanaalStand(zaak, kanaal);
  if (!stand || stand.aan) return null;
  return `${KANAAL_WOORD[kanaal]} staat uit (${stand.reden ?? "niet bereikbaar"}). Kies een ander kanaal.`;
}

/** Houd het aantal bijlagen van de open stand bij voor een wissel. */
function useBewaarBijlagen(bewaar: Bewaarder | undefined, aantal: number) {
  useEffect(() => {
    bewaar?.zet({ bijlagen: aantal });
  }, [bewaar, aantal]);
}

/** De reden die de server geeft als het 24-uursvenster van WhatsApp dicht is. */
const REDEN_VENSTER_DICHT = "venster dicht";

const UIT: BijlageModus = { soort: "uit", reden: "Bijlagen gaan mee met een antwoord per mail" };

/** Naam van de klant uit het gesprek (voor de chip en het kopje). */
function klantNaam(zaak: V6Zaak): string | null {
  // Sessie 9U (C18): eerst de klant van het dossier. Op een telefoonzaak is
  // het laatste "klantbericht" de belnotitie van het callcentrum; die naam
  // ("BizziPhone antwoordservice") stond anders voor het adres van de klant
  // in Aan en als voornaam in een WhatsApp-template.
  if (zaak.klant_naam?.trim()) return zaak.klant_naam.trim();
  for (let i = zaak.berichten.length - 1; i >= 0; i -= 1) {
    const b = zaak.berichten[i];
    if (b.kant === "klant" && b.wie.trim()) return b.wie.trim();
  }
  return null;
}

/** Kan het antwoord in het gesprek zelf (chat of WhatsApp)? */
function isGesprek(kanaal: Kanaal): kanaal is "chat" | "whatsapp" {
  return kanaal === "chat" || kanaal === "whatsapp";
}

function kanaalWoord(kanaal: string, standaard: Kanaal): string {
  return KANAAL_WOORD[(kanaal in KANAAL_WOORD ? kanaal : standaard) as Kanaal];
}

function meldTekst(moment: Moment | null): string {
  return moment === "vandaag_17"
    ? "Gepland voor vandaag 17:00."
    : moment === "morgen_08"
      ? "Gepland voor morgen 08:00."
      : `Verstuurd om ${klokje(Date.now())}.`;
}

/** De van-regel: van wie het concept komt. */
function conceptVan(
  concept: TegelConcept,
): { wie: string | null; van: string; lang: string | null } | null {
  if (!concept.tekst.trim() || concept.bron === "leeg") return null;
  // Sessie 72 (contract blok 2): de verzending mislukte; de tekst uit de
  // wachtrij staat weer in het vak, de kopregel zegt waarom.
  if (concept.bron === "mislukt") {
    const fout = (concept as TegelConcept & { verzend_fout?: string | null }).verzend_fout;
    return { wie: null, van: `Versturen mislukt: ${fout?.trim() || "onbekende fout"}`, lang: null };
  }
  if (concept.bron === "copiloot") {
    return { wie: "C", van: "Aangepast via de copiloot", lang: ", pas verder aan of typ zelf" };
  }
  if (concept.stem === "nova") return { wie: "N", van: "Voorstel van Nova", lang: null };
  return {
    wie: "S",
    van: "Concept van Sanne",
    lang: ", pas aan via de copiloot of typ zelf",
  };
}

/**
 * Na een geslaagde verzending: het vak leeg, en ook het concept in de
 * cache, zodat een standwissel niet de oude tekst terugzet.
 */
function useLeegVak(zaakId: string, leeg: () => void) {
  const queryClient = useQueryClient();
  return () => {
    leeg();
    queryClient.setQueryData<TegelConcept>(v6Sleutels.concept(zaakId), (oud) =>
      oud ? { ...oud, tekst: "", bron: "leeg" } : oud,
    );
  };
}

/** Wat er de deur uit ging, voor de regel "wordt verzonden". */
interface Verzonden {
  kanaal: string;
  tekst: string;
  onderwerp: string | null;
}

/**
 * Sessie 72: de afhandeling na een geslaagd antwoord van de server op
 * Versturen, in één keer en zonder alles te herladen:
 * - het vak leeg (en het concept in de cache);
 * - in de zaak het bericht als "wordt verzonden" (de volger in
 *   `verzending.tsx` zet het om in verzonden of mislukt);
 * - de kaart blijft in de lijstcache van Open, met "Wacht op klant" en
 *   "wordt verzonden" (sinds sessie 82 staat wat op de klant wacht in Open,
 *   niet in Afgerond; de tellers blijven dus gelijk);
 * - één stille herlading van de lijst op de achtergrond;
 * - de melding met Ongedaan maken (de tekst komt dan terug in het vak).
 */
/**
 * Keuze 2 en 7 van 75: na versturen of doorzetten is de zaak niet meer voor JP;
 * het midden springt naar de volgende zaak in de lijst die JP nu ziet, of naar
 * de lege stand "Niets meer voor jou". De lijst zelf (sessie D) volgt de
 * bestaande verversing.
 */
export function useNaarVolgende(zaakId: string) {
  const queryClient = useQueryClient();
  const { kies, lijstFilter } = useV6();
  return () => {
    const kaarten = queryClient.getQueryData<{ kaarten: V6Kaart[] }>(lijstQuery(lijstFilter).queryKey)
      ?.kaarten ?? [];
    const plek = kaarten.findIndex((k) => k.zaak_id === zaakId);
    const na = kaarten.slice(plek + 1).find((k) => k.zaak_id !== zaakId);
    const voor = kaarten.slice(0, Math.max(plek, 0)).find((k) => k.zaak_id !== zaakId);
    kies((na ?? voor)?.zaak_id ?? null);
  };
}

function useNaVersturen(zaak: V6Zaak, leeg: () => void, toonMelding: ToonMelding) {
  const queryClient = useQueryClient();
  const leegVak = useLeegVak(zaak.zaak_id, leeg);
  const zaakId = zaak.zaak_id;
  const naarVolgende = useNaarVolgende(zaakId);
  const { kies } = useV6();
  return (uitkomst: TegelVerstuurd, verzonden: Verzonden, moment: Moment | null) => {
    leegVak();
    void queryClient.invalidateQueries({ queryKey: middenSleutels.midden(zaakId) });
    const verstuurtOp = Date.parse(uitkomst.verstuurt_op);
    voegVerzendingToe(queryClient, {
      wachtrijId: uitkomst.wachtrij_id,
      zaakId,
      kanaal: uitkomst.kanaal || verzonden.kanaal,
      tekst: verzonden.tekst,
      onderwerp: verzonden.onderwerp,
      verstuurtOp: Number.isNaN(verstuurtOp) ? Date.now() + 30_000 : verstuurtOp,
      gepland: moment !== null,
    });
    if (moment === null) {
      werkKaartBij(queryClient, zaakId, {
        groep: "wacht",
        status_woord: WACHT_OP_KLANT,
        toelichting: "wordt verzonden",
        wacht_sinds: null,
      });
      void zetZaak(queryClient, zaakId, {
        groep: "wacht",
        status_woord: WACHT_OP_KLANT,
        wacht_sinds: null,
      });
    } else {
      // Sessie 75 (punt 5): een gepland bericht houdt de zaak in Open, met
      // het tijdstip, zoals de server hem daarna ook toont.
      const woord = moment === "vandaag_17" ? "Gepland 17:00" : "Gepland morgen 08:00";
      werkKaartBij(queryClient, zaakId, { groep: "wacht", status_woord: woord, toelichting: null });
      void zetZaak(queryClient, zaakId, { groep: "wacht", status_woord: woord, wacht_sinds: null });
    }
    planStilleVerversing(queryClient);
    meetNaAanvraag("versturen", /\/(versturen|per-mail|per-gesprek)$/);
    toonMelding({
      tekst: meldTekst(moment),
      tot: uitkomst.ongedaan_tot,
      blijft: true,
      ongedaan: async () => {
        await v6Api.intrekken(uitkomst.wachtrij_id);
        zetVerzending(queryClient, uitkomst.wachtrij_id, { stand: "ingetrokken" });
        naTerugzetten(queryClient, zaakId);
        // Het midden sprong door: terug naar deze zaak, met de tekst weer in het vak.
        kies(zaakId);
      },
    });
    naarVolgende();
  };
}

/** Na Doorzetten: kaart naar Wacht, melding, en door naar de volgende zaak. */
function useNaDoorzetten(zaak: V6Zaak, toonMelding: ToonMelding) {
  const queryClient = useQueryClient();
  const naarVolgende = useNaarVolgende(zaak.zaak_id);
  return (uitkomst: M7Doorgezet) => {
    werkKaartBij(queryClient, zaak.zaak_id, {
      groep: "wacht",
      status_woord: uitkomst.naar === "wacht" ? "Wacht, doorgezet" : "Doorgezet",
      toelichting: null,
    });
    planStilleVerversing(queryClient);
    toonMelding({
      tekst: uitkomst.regel,
      tot: new Date(Date.now() + 8_000).toISOString(),
      ongedaan: null,
      blijft: true,
    });
    naarVolgende();
  };
}

/** Er staat een concept van Sanne of Nova (niet leeg, niet zelf begonnen). */
function heeftConcept(concept: TegelConcept): boolean {
  return Boolean(concept.tekst.trim()) && concept.bron !== "leeg" && concept.bron !== "mislukt";
}

function reOnderwerp(onderwerp: string): string {
  return /^re:/i.test(onderwerp.trim()) ? onderwerp.trim() : `Re: ${onderwerp.trim()}`;
}

// ── Mail ─────────────────────────────────────────────────────────────

/** Wat JP in het mailvak invulde gaat mee bij een kanaalwissel. */
function useBewaarMail(
  bewaar: Bewaarder | undefined,
  velden: MailVeldenStand,
  onderwerp: string,
  actief = true,
) {
  const { aan, cc, bcc } = velden;
  useEffect(() => {
    if (actief) bewaar?.zet({ mail: { aan, cc, bcc, onderwerp } });
  }, [bewaar, aan, cc, bcc, onderwerp, actief]);
}

/**
 * `perMail`: de zaak is geen mailzaak (chat, WhatsApp, bellen of een
 * Beslissen-voorstel op een ander kanaal). De mail gaat dan via het
 * per-mail-pad, dat hem als eigen conversatie aan dezelfde zaak hangt.
 */
function StandMail({
  zaak,
  concept,
  toonMelding,
  keuze,
  bewaar,
  perMail = false,
}: StandProps & { perMail?: boolean }) {
  const zaakId = zaak.zaak_id;
  // Terug uit een ander kanaal: wat JP hier al invulde staat er weer.
  const [vorig] = useState(() => bewaar?.lees().mail ?? null);
  const t = useTekst(zaakId, concept.tekst, vorig?.onderwerp ?? concept.onderwerp ?? zaak.onderwerp);
  const velden = useMailVelden(vorig ?? { aan: concept.aan, cc: concept.cc, bcc: concept.bcc });
  const bijlagen = useBijlagen(zaakId, { soort: "mail" }, concept.concept_bijlagen);
  useBewaarBijlagen(bewaar, bijlagen.lijst.length);
  useBewaarMail(bewaar, velden, t.onderwerp);
  const verzenden = useVerzenden();
  const naVersturen = useNaVersturen(zaak, t.leeg, toonMelding);
  const naDoorzetten = useNaDoorzetten(zaak, toonMelding);
  // Keuze 9 van 75: namens wie, de handtekening onder de tekst, de lijstzinnen.
  const [stem, zetStem] = useStem(concept.stem);
  const { data: midden } = useMidden(zaakId);
  const markeer = useMemo(() => lijstZinnen(midden), [midden]);
  // De editor geeft HTML: lees de tekst, anders ontsnapt &lt;naam&gt; (recensie blok 3).
  const plek = plaatshouderMelding(t.tekst) ?? plaatshouderMelding(tekstVoorControle(t.tekst));
  const van = conceptVan(concept);
  const kan = !verzenden.bezig && Boolean(t.tekst.trim()) && !plek && velden.aan.length > 0;

  const verstuur = (moment: Moment | null) => {
    const mail = {
      ...velden.adressen(),
      onderwerp: t.onderwerp,
      tekst: t.tekst,
      stem,
      bijlage_ids: bijlagen.lijst.map((b) => b.id),
      moment: moment ?? "nu",
    };
    void verzenden.stuur(
      (bevestig): Promise<TegelVerstuurd> => {
        const dubbel = bevestig ? { bevestig_dubbel: true } : {};
        return perMail
          ? v6Api.perMail(zaakId, { ...mail, ...dubbel })
          : v6Api.versturen(zaakId, { kanaal: "mail", ...mail, ...dubbel });
      },
      (uitkomst) => {
        naVersturen(uitkomst, { kanaal: "mail", tekst: t.tekst, onderwerp: t.onderwerp }, moment);
        bijlagen.leeg();
        bewaar?.zet({ mail: null });
      },
    );
  };

  return (
    <div className="antwoord" aria-label="Antwoord per e-mail">
      <Kopregel
        icoon="mail"
        titel={heeftConcept(concept) ? "Concept per e-mail" : "Antwoord per e-mail"}
        {...(van ?? {})}
        rechts={<Namens stem={stem} onStem={zetStem} />}
      />
      {keuze}
      <ConceptRegel zaakId={zaakId} concept={concept} tekst={t.tekst} toonMelding={toonMelding} />
      <MailVelden
        velden={velden}
        naam={klantNaam(zaak)}
        onderwerp={t.onderwerp}
        onOnderwerp={t.zetOnderwerp}
        tekst={t.tekst}
        onTekst={t.zetTekst}
        bijlagen={bijlagen}
        markeer={markeer}
        onder={<Handtekening stem={stem} tekst={t.tekst} />}
      />
      <VakFout
        fout={verzenden.fout}
        melding={plek}
        dubbel={verzenden.dubbel}
        onNiet={verzenden.sluitDubbel}
      />
      <Voet
        zaak={zaak}
        bijlagen={bijlagen}
        onLater={kan ? (m) => verstuur(m) : undefined}
        onFout={verzenden.zetFout}
        hoofdknop={
          <Balk
            zaakId={zaakId}
            tekst={() => htmlNaarTekst(t.tekst)}
            onAkkoord={() => verstuur(null)}
            akkoordUit={!kan}
            collegaKan
            nietGoedKan={heeftConcept(concept)}
            onDoorgezet={naDoorzetten}
          />
        }
      />
    </div>
  );
}

// ── WhatsApp beginnen in een telefoonzaak (sessie 9U, C18) ──────────

/**
 * Een telefoonzaak (belnotitie van het callcentrum of een gesprek) zonder
 * WhatsApp-gesprek. JP begint het gesprek hier, in dezelfde zaak; daarna
 * opent het vak met een template (buiten 24 uur) of vrije tekst. Er gaat
 * bij deze knop nog niets naar de klant, en nooit iets naar het callcentrum.
 */
function StandWhatsAppBeginnen({ zaak, keuze, toonMelding }: StandProps) {
  const zaakId = zaak.zaak_id;
  const { kies } = useV6();
  const queryClient = useQueryClient();
  const nummer = kanaalStand(zaak, "whatsapp")?.adres ?? null;
  // Het nummer waarop de draad echt start, leesbaar: "31612399001" wordt
  // "06 12399001". Niet het belnummer: dat kan een ander toestel zijn.
  const leesbaar = nummer && /^316\d{8}$/.test(nummer) ? `06 ${nummer.slice(3)}` : nummer;
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const [ander, setAnder] = useState<string | null>(null);
  const begin = async () => {
    if (bezig) return;
    setBezig(true);
    setFout(null);
    try {
      const uit = await v6Api.whatsappInZaak(zaakId);
      if (uit.andere_zaak_id) {
        setAnder(uit.andere_zaak_id);
        return;
      }
      verversZaak(queryClient, zaakId);
      toonMelding({
        tekst: "WhatsApp staat klaar in deze zaak.",
        tot: new Date(Date.now() + 6_000).toISOString(),
        ongedaan: null,
      });
    } catch (e) {
      setFout(foutTekst(e, "WhatsApp beginnen lukte niet. Probeer het opnieuw."));
    } finally {
      setBezig(false);
    }
  };
  return (
    <div className="antwoord" aria-label="WhatsApp beginnen">
      <Kopregel icoon="whatsapp" titel="WhatsApp beginnen" />
      {keuze}
      <div className="lijf">
        <p className="stil">
          Er is nog geen WhatsApp-gesprek met deze klant. Begin het hier, in deze zaak
          {leesbaar ? `, naar ${leesbaar}` : ""}. Buiten 24 uur gaat het eerste bericht als
          goedgekeurde template. Bellen en mailen blijven in dezelfde zaak.
        </p>
        {ander && (
          <p className="kanaalregel" role="status">
            Het WhatsApp-gesprek van dit nummer loopt al in een andere open zaak.{" "}
            <button type="button" className="v6-knop" onClick={() => kies(ander, "whatsapp")}>
              Open die zaak
            </button>
          </p>
        )}
        {fout && (
          <p className="v6-fout" role="alert">
            {fout}
          </p>
        )}
      </div>
      <div className="voet">
        <button
          type="button"
          className="v6-knop acc"
          disabled={bezig || !nummer}
          onClick={() => void begin()}
        >
          <Icoon naam="whatsapp" klein />
          WhatsApp beginnen
        </button>
      </div>
    </div>
  );
}

// ── Chat en WhatsApp ─────────────────────────────────────────────────

/**
 * Antwoord in het chat- of WhatsApp-gesprek van de zaak. Het kanaal komt
 * uit de kanaalkeuze; de oude Via-regel met "wijzig" is daarin opgegaan
 * (sessie 70, punt 13), dus hier is geen tweede keuze meer.
 */
function StandChat({
  zaak,
  concept,
  toonMelding,
  keuze,
  bewaar,
  kanaal,
}: StandProps & { kanaal: "chat" | "whatsapp" }) {
  const zaakId = zaak.zaak_id;
  const t = useTekst(zaakId, concept.tekst, zaak.onderwerp);
  // De sessie van dít kanaal: een zaak kan webchat én WhatsApp hebben, en
  // een WhatsApp-bestand gaat meteen weg (recensie punt 13).
  const sessieId = kanaalStand(zaak, kanaal)?.sessie_id ?? zaak.live?.sessie_id ?? null;
  const modus: BijlageModus =
    kanaal === "whatsapp"
      ? sessieId
        ? { soort: "whatsapp", sessieId }
        : { soort: "uit", reden: "Geen WhatsApp-gesprek open" }
      : { soort: "uit", reden: "Bijlagen gaan per mail of WhatsApp" };
  const bijlagen = useBijlagen(zaakId, modus);
  useBewaarBijlagen(bewaar, bijlagen.lijst.length);
  const queryClient = useQueryClient();
  const verzenden = useVerzenden();
  const naVersturen = useNaVersturen(zaak, t.leeg, toonMelding);
  const naDoorzetten = useNaDoorzetten(zaak, toonMelding);
  const tekstRef = useRef<HTMLTextAreaElement>(null);
  const plek = plaatshouderMelding(t.tekst);
  const uit = kanaalUit(zaak, kanaal);
  const van = conceptVan(concept);
  const kan = !verzenden.bezig && Boolean(t.tekst.trim()) && !plek && !uit;
  // Sessie 72 (punt 10): WhatsApp met een gesprek maar een dicht venster.
  // Vrije tekst mag niet; een goedgekeurde template wel, in dit gesprek.
  // Alleen bij die reden (`werkplek_v6._kanalen`); staat WhatsApp om een
  // andere reden uit, dan zegt het vak die reden.
  const waStand = kanaalStand(zaak, "whatsapp");
  const venster =
    kanaal === "whatsapp" &&
    Boolean(sessieId) &&
    waStand?.aan === false &&
    waStand.reden === REDEN_VENSTER_DICHT;
  const template = useTemplateKeuze(venster ? sessieId : null, klantNaam(zaak)?.split(" ")[0] ?? null);

  const stuurTemplate = () =>
    void verzenden.stuur(
      () => template.verstuur(),
      () => {
        template.leeg();
        // De template gaat direct naar Meta, zonder wachtrij: zaak en lijst
        // halen het bericht zelf op (sessie 72, geen breed herladen meer).
        verversZaak(queryClient, zaakId);
        planStilleVerversing(queryClient, 0);
        toonMelding({
          tekst: "Template verstuurd.",
          tot: new Date(Date.now() + 5_000).toISOString(),
          ongedaan: null,
        });
      },
    );

  const verstuur = (moment: Moment | null) =>
    void verzenden.stuur(
      (bevestig): Promise<TegelVerstuurd> =>
        v6Api.perGesprek(zaakId, {
          kanaal,
          tekst: t.tekst,
          ...(moment ? { moment } : {}),
          stem: concept.stem,
          ...(bevestig ? { bevestig_dubbel: true } : {}),
        }),
      (uitkomst) => {
        naVersturen(uitkomst, { kanaal, tekst: t.tekst, onderwerp: null }, moment);
        bijlagen.leeg();
      },
    );

  return (
    <div className="antwoord" aria-label="Antwoord">
      <Kopregel
        icoon={kanaal}
        titel={
          heeftConcept(concept)
            ? `Concept per ${kanaal === "whatsapp" ? "WhatsApp" : "chat"}`
            : "Antwoord"
        }
        {...(van ?? {})}
      />
      {keuze}
      {venster ? (
        <TemplateKeuze keuze={template} />
      ) : (
        <>
          <ConceptRegel zaakId={zaakId} concept={concept} tekst={t.tekst} toonMelding={toonMelding} />
          <BijlageChips bijlagen={bijlagen} />
          <div className="lijf">
            <Tekstvak
              ref={tekstRef}
              aria-label="Antwoord"
              placeholder="Typ je antwoord"
              waarde={t.tekst}
              onWaarde={t.zetTekst}
            />
          </div>
          <Werkbalk tekstRef={tekstRef} tekst={t.tekst} onTekst={t.zetTekst} />
        </>
      )}
      <VakFout
        fout={verzenden.fout}
        melding={venster ? template.melding : (plek ?? uit)}
        dubbel={verzenden.dubbel}
        onNiet={verzenden.sluitDubbel}
      />
      <Voet
        zaak={zaak}
        bijlagen={bijlagen}
        onLater={kan && !venster ? (m) => verstuur(m) : undefined}
        onFout={verzenden.zetFout}
        hoofdknop={
          venster ? (
            <Hoofdknop
              icoon="stuur"
              disabled={!template.kan || verzenden.bezig}
              onClick={stuurTemplate}
            >
              Template versturen
            </Hoofdknop>
          ) : (
            <Balk
              zaakId={zaakId}
              tekst={() => t.tekst}
              onAkkoord={() => verstuur(null)}
              akkoordUit={!kan}
              collegaKan={false}
              nietGoedKan={heeftConcept(concept)}
              onDoorgezet={naDoorzetten}
            />
          )
        }
      />
    </div>
  );
}

// ── Beslissen ────────────────────────────────────────────────────────

/** De eerste zin: het voorstel staat in één zin boven de uitleg. */
function eenZin(tekst: string | null | undefined): string | null {
  const schoon = (tekst ?? "").replace(/\s+/g, " ").trim();
  if (!schoon) return null;
  const eind = schoon.search(/[.!?](\s|$)/);
  return eind === -1 ? schoon : schoon.slice(0, eind + 1);
}

/** "Anders, ik typ zelf": geen vraag aan de copiloot, JP schrijft. */
function isZelf(tekst: string): boolean {
  return /^anders\b/i.test(tekst.trim());
}

/**
 * Sessie 53: de stoplichtstand van deze zaak zoals de lijst hem nu kent.
 * De zaak zelf draagt geen stand; de kaart in de lijst wel. We abonneren
 * op precies de query van de huidige map en filter, zodat een oude lijst
 * (een andere map, een verouderde kopie) nooit "Akkoord en verwerk" geeft.
 * Staat de zaak niet in die lijst, dan is het "Akkoord en versturen".
 */
function useKaartStand(zaakId: string): V6Kaart["stand"] {
  const { lijstFilter } = useV6();
  // Sessie 72: geen eigen polling; de lijst zelf ververst (live, of elke
  // minuut als de live-stroom dicht is).
  const lijst = useQuery(lijstQuery(lijstFilter));
  return lijst.data?.kaarten?.find((k) => k.zaak_id === zaakId)?.stand ?? null;
}

/**
 * Beslissen volgens ontwerp 26: voorstel in één zin, uitleg, keuzes als
 * aanvinkrondjes met het advies bovenaan en vooraf gekozen, daaronder het
 * bericht (bewerkbaar), en rechtsonder op de vaste plek de oranje knop
 * Akkoord en versturen. Ontbreekt een onderdeel in de beslis-kaart van de
 * motor, dan valt die regel weg: nooit een lege kop of een leeg blok.
 */
/**
 * Beslissen. Het voorstel (keuzes, actie) blijft staan bij elke
 * kanaalwissel behalve Bellen; alleen het kanaal van het bericht wisselt
 * (recensie punt 13). Via het eigen kanaal gaat het zoals altijd; via een
 * ander kanaal langs het per-mail- of per-gesprek-pad, met de actie mee.
 */
function StandBeslis({
  zaak,
  concept,
  toonMelding,
  keuze: kanaalKeuze,
  bewaar,
  kanaal,
  eigen,
}: StandProps & { kanaal: Exclude<Kanaal, "telefoon">; eigen: Kanaal }) {
  const zaakId = zaak.zaak_id;
  const { vraagCopiloot } = useV6();
  // Sessie 72 (punt 9): bij E-mail het complete mailvak, zelfde component
  // als de mailstand; bij chat en WhatsApp het platte berichtvak.
  const perMailvak = kanaal === "mail";
  const [vorig] = useState(() => bewaar?.lees().mail ?? null);
  const t = useTekst(zaakId, concept.tekst, vorig?.onderwerp ?? concept.onderwerp ?? zaak.onderwerp);
  const velden = useMailVelden(
    vorig ?? { aan: concept.aan, cc: concept.cc, bcc: concept.bcc },
    perMailvak,
  );
  useBewaarMail(bewaar, velden, t.onderwerp, perMailvak);
  // Weg uit het mailvak: de editor gaf HTML, het chatvak wil platte tekst.
  const [vorigKanaal, setVorigKanaal] = useState(kanaal);
  if (kanaal !== vorigKanaal) {
    setVorigKanaal(kanaal);
    if (vorigKanaal === "mail") t.zetTekst(htmlNaarTekst(t.tekst));
  }
  const beslis = zaak.beslis;
  const kaartStand = useKaartStand(zaakId);
  // Advies bovenaan, ook als de server de volgorde ooit anders levert.
  const keuzes = [...(beslis?.keuzes ?? [])]
    .filter((k) => k.tekst.trim())
    .sort((a, b) => Number(b.advies) - Number(a.advies));
  // De geadviseerde keuze: alleen die voert de actie van Nova uit; kiest
  // JP iets anders, dan regelt JP het zelf.
  const advies = keuzes.findIndex((k) => k.advies);
  const [keuze, setKeuze] = useState(() => Math.max(0, advies));
  const actie = beslis?.actie ?? null;
  const voertActieUit = Boolean(actie) && (keuzes.length === 0 || keuze === advies);
  const bijlagen = useBijlagen(
    zaakId,
    perMailvak ? { soort: "mail" } : UIT,
    concept.concept_bijlagen,
  );
  useBewaarBijlagen(bewaar, perMailvak ? bijlagen.lijst.length : 0);
  const verzenden = useVerzenden();
  const naVersturen = useNaVersturen(zaak, t.leeg, toonMelding);
  const naDoorzetten = useNaDoorzetten(zaak, toonMelding);
  // Keuze 9 van 75 ook in Beslissen: namens wie, handtekening, lijstzinnen (mail).
  const [stem, zetStem] = useStem(concept.stem);
  const { data: midden } = useMidden(zaakId);
  const markeer = useMemo(() => lijstZinnen(midden), [midden]);
  const tekstRef = useRef<HTMLTextAreaElement>(null);
  const plek = perMailvak
    ? (plaatshouderMelding(t.tekst) ?? plaatshouderMelding(tekstVoorControle(t.tekst)))
    : plaatshouderMelding(t.tekst);
  const zonderAan =
    perMailvak && velden.aan.length === 0 ? "Er staat geen ontvanger in het Aan-veld" : null;
  const uit = kanaalUit(zaak, kanaal);
  const voornaam = klantNaam(zaak)?.split(" ")[0] ?? "de klant";
  const kan = !verzenden.bezig && Boolean(t.tekst.trim()) && !plek && !zonderAan && !uit;
  const voorstel = eenZin(beslis?.voorstel);
  const uitleg = beslis?.uitleg?.trim() || null;
  const metBlok = Boolean(voorstel || uitleg || keuzes.length || actie);

  const kiesKeuze = (i: number) => {
    if (i === keuze) return;
    setKeuze(i);
    if (isZelf(keuzes[i].tekst)) {
      tekstRef.current?.focus();
      return;
    }
    vraagCopiloot(`Pas het bericht aan voor deze keuze: ${keuzes[i].tekst}`);
  };

  const actieId = voertActieUit && actie ? actie.id : null;
  const stuur = (bevestig: boolean): Promise<TegelVerstuurd> => {
    const dubbel = bevestig ? { bevestig_dubbel: true } : {};
    // Het mailvak: Aan, CC, BCC, onderwerp en bijlagen gaan mee, over
    // dezelfde route als altijd (eigen kanaal `versturen`, anders `perMail`).
    const mail = perMailvak
      ? {
          ...velden.adressen(),
          onderwerp: t.onderwerp,
          bijlage_ids: bijlagen.lijst.map((b) => b.id),
        }
      : null;
    if (kanaal === eigen) {
      return v6Api.versturen(zaakId, {
        kanaal: concept.kanaal,
        tekst: t.tekst,
        aan: concept.aan,
        onderwerp: concept.onderwerp,
        ...(mail ?? {}),
        stem: mail ? stem : concept.stem,
        actie_id: actieId,
        uit_beslissen: true,
        ...dubbel,
      });
    }
    if (mail) {
      return v6Api.perMail(zaakId, {
        ...mail,
        tekst: t.tekst,
        stem,
        moment: "nu",
        actie_id: actieId,
        uit_beslissen: true,
        ...dubbel,
      });
    }
    return v6Api.perGesprek(zaakId, {
      kanaal: kanaal as "chat" | "whatsapp",
      tekst: t.tekst,
      stem: concept.stem,
      actie_id: actieId,
      uit_beslissen: true,
      ...dubbel,
    });
  };

  const akkoord = () =>
    void verzenden.stuur(
      stuur,
      (uitkomst) => {
        naVersturen(
          uitkomst,
          { kanaal, tekst: t.tekst, onderwerp: perMailvak ? t.onderwerp : null },
          null,
        );
        bijlagen.leeg();
        bewaar?.zet({ mail: null });
      },
    );

  return (
    <div className="antwoord beslis" aria-label="Beslissen">
      <Kopregel icoon="ok" titel="Beslissen" wie="N" van="Voorstel van Nova" />
      {kanaalKeuze}
      {metBlok && (
        <div className="beslisblok" data-testid="v6-beslisblok">
          {voorstel && <div className="wat">{voorstel}</div>}
          {uitleg && <div className="hoe">{uitleg}</div>}
          {keuzes.length > 0 && (
            <div role="radiogroup" aria-label="Keuzes" className="keuzes">
              {keuzes.map((k, i) => (
                <button
                  key={`${i}-${k.tekst}`}
                  type="button"
                  role="radio"
                  aria-checked={i === keuze}
                  className={`keuze${i === keuze ? " on" : ""}${k.advies ? " advies" : ""}`}
                  onClick={() => kiesKeuze(i)}
                >
                  <span className="rond" aria-hidden="true" />
                  <span className="keuzetekst">{k.tekst}</span>
                  {k.advies && <span className="adviesmerk">advies</span>}
                  {k.detail && <small>{k.detail}</small>}
                  {i === advies && actie && (
                    <small className="voertuit">voert uit: {actie.label}</small>
                  )}
                </button>
              ))}
            </div>
          )}
          {keuzes.length === 0 && actie && <div className="voertuit">voert uit: {actie.label}</div>}
        </div>
      )}
      <div className="kopje">
        Bericht aan {voornaam} via {kanaalWoord(kanaal, zaak.kanaal)} bij akkoord
        {perMailvak && <Namens stem={stem} onStem={zetStem} />}
      </div>
      <ConceptRegel zaakId={zaakId} concept={concept} tekst={t.tekst} toonMelding={toonMelding} />
      {perMailvak ? (
        <MailVelden
          velden={velden}
          naam={klantNaam(zaak)}
          onderwerp={t.onderwerp}
          onOnderwerp={t.zetOnderwerp}
          tekst={t.tekst}
          onTekst={t.zetTekst}
          bijlagen={bijlagen}
          tekstLabel="Bericht bij akkoord"
          markeer={markeer}
          onder={<Handtekening stem={stem} tekst={t.tekst} />}
        />
      ) : (
        <div className="lijf">
          <Tekstvak
            ref={tekstRef}
            className="kort"
            aria-label="Bericht bij akkoord"
            placeholder="Typ het bericht aan de klant"
            waarde={t.tekst}
            onWaarde={t.zetTekst}
          />
        </div>
      )}
      <VakFout
        fout={verzenden.fout}
        melding={plek ?? zonderAan ?? uit}
        dubbel={verzenden.dubbel}
        onNiet={verzenden.sluitDubbel}
      />
      <Voet
        zaak={zaak}
        bijlagen={bijlagen}
        onFout={verzenden.zetFout}
        hoofdknop={
          kaartStand === "akkoord" ? (
            <Hoofdknop icoon="ok" disabled={!kan} onClick={akkoord}>
              Akkoord en verwerk
            </Hoofdknop>
          ) : (
            <Balk
              zaakId={zaakId}
              tekst={() => (perMailvak ? htmlNaarTekst(t.tekst) : t.tekst)}
              onAkkoord={akkoord}
              akkoordUit={!kan}
              collegaKan={perMailvak}
              nietGoedKan
              onDoorgezet={naDoorzetten}
            />
          )
        }
      />
    </div>
  );
}

// ── Terugbellen ──────────────────────────────────────────────────────

const UITKOMSTEN: { waarde: TerugbelUitkomst; woord: string }[] = [
  { waarde: "afgehandeld", woord: "Afgehandeld" },
  { waarde: "niet_bereikt", woord: "Niet bereikt, Sanne stuurt een bericht" },
  { waarde: "later", woord: "Later nog eens bellen" },
];

/** Terugval als de server geen eigen zin meestuurt (netwerkfout, 5xx). */
const BEL_FOUT_VAST = "Bellen is niet gelukt. Probeer het opnieuw of bel handmatig.";

function StandBel({ zaak, concept, toonMelding, keuze }: StandProps) {
  const zaakId = zaak.zaak_id;
  const t = useTekst(zaakId, concept.tekst, "Ons telefoongesprek");
  const [notitie, setNotitie] = useState("");
  // Op een zaak zonder terugbelverzoek kiest JP de uitkomst zelf: Afgehandeld
  // rondt de hele zaak af, dat mag niet vanzelf (recensie punt 13).
  const [uitkomst, setUitkomst] = useState<TerugbelUitkomst | null>(
    zaak.stand === "bel" ? "afgehandeld" : null,
  );
  const [belBezig, setBelBezig] = useState(false);
  const [gebeld, setGebeld] = useState(false);
  // Sessie 20261023: de fout van de belknop staat direct onder de knop,
  // niet onderaan bij Afronden (op de iPhone buiten beeld).
  const [belFout, setBelFout] = useState<string | null>(null);
  const belLoopt = useRef(false);
  const bijlagen = useBijlagen(zaakId, UIT);
  const verzenden = useVerzenden();
  const leegVak = useLeegVak(zaakId, t.leeg);
  const queryClient = useQueryClient();
  // Sessie 70 (punt 13): ook als JP Bellen kiest op een zaak zonder
  // terugbelverzoek, met het nummer en adres uit de kanaalkeuze.
  const nummer = zaak.bel?.nummer ?? kanaalStand(zaak, "telefoon")?.adres ?? null;
  const adres = concept.aan[0] || zaak.via?.aan || kanaalStand(zaak, "mail")?.adres || null;
  // Controle 72: er gaat alleen een bevestiging mee als JP die aanvinkt.
  const [mailen, setMailen] = useState(false);
  const kanMailen = Boolean(t.tekst.trim() && adres);
  const metBevestiging = mailen && kanMailen;
  const zonderKlant = !(zaak.klant_id ?? zaak.bel?.klant_id);
  // Na koppelen: een regel met "Naar de kaart" (alleen bij deze zaak).
  const [gekoppeld, setGekoppeld] = useState<Gekoppeld | null>(null);
  const plek = metBevestiging ? plaatshouderMelding(t.tekst) : null;
  const zonderUitkomst = uitkomst ? null : "Kies de uitkomst van het gesprek.";
  const kan = !verzenden.bezig && !plek && Boolean(uitkomst);

  const bel = async () => {
    // Grendel tegen een dubbele tik: `belBezig` is pas na de render waar.
    if (!nummer || belLoopt.current) return;
    belLoopt.current = true;
    setBelBezig(true);
    setBelFout(null);
    try {
      await v6Api.bellen({
        nummer,
        klant_id: zaak.bel?.klant_id ?? zaak.klant_id,
        zaak_id: zaakId,
      });
      setGebeld(true);
    } catch (e) {
      const zin = foutTekst(e, BEL_FOUT_VAST);
      setBelFout(zin);
      // Ook als toast: valt de regel buiten beeld, dan ziet JP het toch.
      // Een toetsaccount-weigering had al een eigen toast.
      if (!isToetsWeigering(e)) toast.error(zin, { id: "bellen-fout" });
    } finally {
      belLoopt.current = false;
      setBelBezig(false);
    }
  };

  const rondAf = () => {
    if (!uitkomst) return;
    void verzenden.stuur(
      () =>
        v6Api.terugbellen(zaakId, {
          uitkomst,
          notitie: notitie.trim() || null,
          bevestiging:
            metBevestiging && adres
              ? { aan: [adres], onderwerp: "Ons telefoongesprek", tekst: t.tekst }
              : null,
        }),
      (antwoord) => {
        leegVak();
        setNotitie("");
        setMailen(false);
        // Sessie 72: alleen deze zaak en zijn kaart, niet alles opnieuw.
        if (antwoord.afgerond) void naAfronden(queryClient, zaakId, "sluiten", zaak.kanaal);
        else {
          verversZaak(queryClient, zaakId);
          planStilleVerversing(queryClient, 0);
        }
        toonMelding({
          tekst: antwoord.mail_klaargezet
            ? "Afgerond, bevestiging staat klaar."
            : "Afgerond.",
          tot: antwoord.ongedaan_tot ?? new Date(Date.now() + 10_000).toISOString(),
          ongedaan: antwoord.event_id
            ? async () => {
                await v6Api.ongedaan(zaakId, antwoord.event_id as string);
                naTerugzetten(queryClient, zaakId);
              }
            : null,
        });
      },
    );
  };

  return (
    <div className="antwoord" aria-label="Terugbellen">
      <Kopregel
        icoon="bel"
        titel={zaak.stand === "bel" ? "Terugbellen" : "Bellen"}
        {...(conceptVan(concept) ?? {})}
      />
      {keuze}
      <div className="belblok">
        <div>
          <div className="nr">{nummer ?? "Geen nummer"}</div>
          {zaak.bel?.tijdvak && <div className="tijd">{zaak.bel.tijdvak}</div>}
        </div>
        <button
          type="button"
          className="v6-knop acc"
          disabled={!nummer || belBezig}
          onClick={() => void bel()}
        >
          <Icoon naam="bel" klein />
          {gebeld ? "Bel opnieuw" : "Bel via Rinkel"}
        </button>
      </div>
      {belFout && (
        <p className="v6-fout" role="alert">
          {belFout}
        </p>
      )}
      {gekoppeld && gekoppeld.zaakId === zaakId ? (
        <KlantGekoppeld gekoppeld={gekoppeld} />
      ) : (
        zonderKlant && <KlantKoppelen zaakId={zaakId} onGekoppeld={setGekoppeld} />
      )}
      <div className="kopje">Notitie van het gesprek</div>
      <div className="lijf">
        <Tekstvak
          className="notitie"
          aria-label="Notitie van het gesprek"
          placeholder="Notitie"
          waarde={notitie}
          onWaarde={setNotitie}
        />
      </div>
      <div className="kopje">Uitkomst</div>
      <div className="uitkomst" role="radiogroup" aria-label="Uitkomst">
        {UITKOMSTEN.map((u) => (
          <button
            key={u.waarde}
            type="button"
            role="radio"
            aria-checked={uitkomst === u.waarde}
            className={`keuze${uitkomst === u.waarde ? " on" : ""}`}
            onClick={() => setUitkomst(u.waarde)}
          >
            <span className="rond" />
            {u.woord}
          </button>
        ))}
      </div>
      <div className="kopje">Bevestiging na het gesprek, per e-mail</div>
      {adres ? (
        <>
          <div className="bevestigaan">naar {adres}</div>
          <div className="lijf">
            <Tekstvak
              className="kort"
              aria-label="Bevestiging per e-mail"
              waarde={t.tekst}
              onWaarde={t.zetTekst}
            />
          </div>
        </>
      ) : (
        <div className="bevestigaan">Geen e-mailadres bekend</div>
      )}
      {kanMailen && (
        <label className="belmailen">
          <input
            type="checkbox"
            checked={mailen}
            onChange={(e) => setMailen(e.target.checked)}
          />
          Bevestiging mailen
        </label>
      )}
      <VakFout
        fout={verzenden.fout}
        melding={plek ?? zonderUitkomst}
        dubbel={verzenden.dubbel}
        onNiet={verzenden.sluitDubbel}
      />
      <Voet
        zaak={zaak}
        bijlagen={bijlagen}
        onFout={verzenden.zetFout}
        hoofdknop={
          <Hoofdknop icoon="ok" disabled={!kan} onClick={rondAf}>
            {metBevestiging ? "Afronden en bevestiging mailen" : "Afronden"}
          </Hoofdknop>
        }
      />
    </div>
  );
}

/**
 * "Klant koppelen" in de belstand (controle 72, sessie 73 blok 3): een
 * onbekend nummer heeft geen klant, dus de notitie van het gesprek kan
 * nergens landen. JP zoekt op naam, postcode of nummer; per klant naam,
 * woonplaats, bestellingen en dieren. Een klik koppelt nog niet: eerst één
 * bevestigingsregel met "Koppelen" en "Terug". Na koppelen blijft een
 * regel staan met "Naar de kaart". Op de iPhone is het zoekvak een vlak
 * met een kruisje.
 */
function KlantKoppelen({
  zaakId,
  onGekoppeld,
}: {
  zaakId: string;
  onGekoppeld: (g: Gekoppeld) => void;
}) {
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <div className="belkoppel">
        <button type="button" className="v6-knop" onClick={() => setOpen(true)}>
          <Icoon naam="klant" klein />
          Klant koppelen
        </button>
      </div>
    );
  }
  return (
    <KlantKoppelVlak
      stijl="bel"
      zaakId={zaakId}
      onSluit={() => setOpen(false)}
      onGekoppeld={(g) => {
        onGekoppeld(g);
        setOpen(false);
      }}
    />
  );
}

function KlantGekoppeld({ gekoppeld }: { gekoppeld: Gekoppeld }) {
  return (
    <div className="belkoppel belgekoppeld" role="status">
      <span>
        Gekoppeld aan <b>{gekoppeld.naam}</b>
      </span>
      <Link className="belkoppel-link" to={`/klanten/${gekoppeld.klantId}`}>
        Naar de kaart
      </Link>
    </div>
  );
}


// ── Live ─────────────────────────────────────────────────────────────

function StandLive({ zaak }: { zaak: V6Zaak }) {
  const queryClient = useQueryClient();
  const bijlagen = useBijlagen(zaak.zaak_id, { soort: "uit", reden: "Neem eerst over" });
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);

  const neemOver = async () => {
    if (!zaak.live) return;
    setBezig(true);
    setFout(null);
    try {
      await v6Api.overnemen(zaak.live.sessie_id);
      await queryClient.invalidateQueries({ queryKey: v6Sleutels.zaak(zaak.zaak_id) });
      void queryClient.invalidateQueries({ queryKey: v6Sleutels.lijst });
    } catch (e) {
      setFout(foutTekst(e, "Overnemen lukte niet. Probeer het opnieuw."));
    } finally {
      setBezig(false);
    }
  };

  return (
    <div className="antwoord" aria-label="Live">
      {/* Sessie 70, punt 6: geen uitlegzinnen, alleen wie antwoordt en Neem over. */}
      <Kopregel icoon={zaak.kanaal} titel="Live" wie="N" van="Nova antwoordt" />
      <VakFout fout={fout} dubbel={null} onNiet={() => undefined} />
      <Voet
        zaak={zaak}
        bijlagen={bijlagen}
        onFout={setFout}
        hoofdknop={
          <Hoofdknop icoon="klant" disabled={!zaak.live || bezig} onClick={() => void neemOver()}>
            Neem over
          </Hoofdknop>
        }
      />
    </div>
  );
}

// ── Geen open gesprek ────────────────────────────────────────────────

/**
 * Sessie 70, punt 8: een zaak waarvan het gesprek is losgemaakt (of die er
 * nooit een had) toont de berichten en een lege antwoordstand. Notitie en
 * het menu blijven; versturen kan niet, en de kopregel zegt waarom.
 */
function StandGeenGesprek({ zaak, concept, toonMelding, keuze }: StandProps) {
  const bijlagen = useBijlagen(zaak.zaak_id, UIT);
  const [fout, setFout] = useState<string | null>(null);
  return (
    <div className="antwoord" aria-label="Geen open gesprek">
      <Kopregel icoon="antwoord" titel="Geen open gesprek" />
      {keuze}
      {/* Een afrondvoorstel staat ook hier, zoals kaart en kop het noemen. */}
      <ConceptRegel zaakId={zaak.zaak_id} concept={concept} tekst="" toonMelding={toonMelding} />
      <VakFout fout={fout} dubbel={null} onNiet={() => undefined} />
      <Voet
        zaak={zaak}
        bijlagen={bijlagen}
        onFout={setFout}
        hoofdknop={
          <Hoofdknop icoon="stuur" disabled onClick={() => undefined}>
            Versturen
          </Hoofdknop>
        }
      />
    </div>
  );
}

// ── Afgerond ─────────────────────────────────────────────────────────

function StandAfgerond({ zaak }: { zaak: V6Zaak }) {
  const bijlagen = useBijlagen(zaak.zaak_id, { soort: "uit", reden: "Deze zaak is afgerond" });
  const [fout, setFout] = useState<string | null>(null);
  return (
    <div className="antwoord" aria-label="Afgerond">
      <Kopregel icoon="ok" titel="Afgerond" />
      <div className="standregel">
        <p>{zaak.status_woord}</p>
      </div>
      <VakFout fout={fout} dubbel={null} onNiet={() => undefined} />
      <Voet zaak={zaak} bijlagen={bijlagen} onFout={setFout} />
    </div>
  );
}

// ── Keuze van de stand ───────────────────────────────────────────────

function leegConcept(zaak: V6Zaak): TegelConcept {
  return {
    zaak_id: zaak.zaak_id,
    kanaal: zaak.kanaal === "telefoon" ? "mail" : zaak.kanaal,
    aan: zaak.via?.aan ? [zaak.via.aan] : [],
    cc: [],
    bcc: [],
    onderwerp: null,
    tekst: "",
    handtekening_html: "",
    handtekening_tekst: "",
    stem: "sanne",
    bijlagen: [],
    bron: "leeg",
    stoplicht: "oranje",
    vertrouwen: null,
    reden_leeg: null,
  };
}

// ── Kanaalkeuze (sessie 70, punt 13) ────────────────────────────────

function isKanaal(waarde: string | null | undefined): waarde is Kanaal {
  return KANALEN.includes(waarde as Kanaal);
}

function kanaalStand(zaak: V6Zaak, kanaal: Kanaal) {
  return zaak.kanalen?.find((k) => k.kanaal === kanaal) ?? null;
}

/** Het kanaal dat bij de stand van de server hoort. */
function eigenKanaal(zaak: V6Zaak, concept: TegelConcept): Kanaal {
  switch (zaak.stand) {
    case "beslis":
      return isKanaal(concept.kanaal) ? concept.kanaal : zaak.kanaal;
    case "bel":
      return "telefoon";
    case "mail":
      return "mail";
    default: {
      // Chat: waar het antwoord volgens de server heen gaat (via).
      const via = zaak.via?.kanaal;
      if (via && isKanaal(via)) return via;
      return isGesprek(zaak.kanaal) ? zaak.kanaal : "chat";
    }
  }
}

/**
 * Het kanaal waarmee het vak opent. Terugbellen: bellen. Beslissen: het
 * kanaal van het voorstel, zolang dat aan staat. Anders het kanaal van het
 * laatste klantbericht; staat dat uit (WhatsApp-venster dicht, chat
 * gesloten), dan het kanaal van de server, en anders het eerste dat aan
 * staat. Staat niets aan, dan zegt de stand zelf waarom er niets heen kan.
 */
function beginKanaal(zaak: V6Zaak, concept: TegelConcept): Kanaal {
  const eigen = eigenKanaal(zaak, concept);
  if (!zaak.kanalen?.length || zaak.stand === "bel") return eigen;
  const aan = (k: Kanaal) => Boolean(kanaalStand(zaak, k)?.aan);
  if (zaak.stand === "beslis" && aan(eigen)) return eigen;
  const standaard = zaak.kanaal_standaard ?? eigen;
  // Bellen opent nooit vanzelf buiten een terugbelzaak: dat is een
  // handeling, geen antwoord.
  const antwoord = (k: Kanaal) => aan(k) && k !== "telefoon";
  if (antwoord(standaard)) return standaard;
  if (antwoord(eigen)) return eigen;
  return KANALEN.find(antwoord) ?? (standaard === "telefoon" ? eigen : standaard);
}

/**
 * Het concept voor de stand van het gekozen kanaal. Het kanaal van de
 * server houdt zijn eigen concept; een ander kanaal begint met de tekst
 * die JP al had (of die van het voorstel), en bij mail met Aan uit de
 * kanaalkeuze en "Re: <onderwerp>".
 */
function conceptVoor(
  zaak: V6Zaak,
  concept: TegelConcept,
  kanaal: Kanaal,
  eigen: Kanaal,
  meeTekst: string | null,
): TegelConcept {
  const tekst = meeTekst ?? concept.tekst;
  // Het concept van de server hoort bij dit kanaal: het blijft zoals het
  // is (Aan, onderwerp, stem). Bij Terugbellen is het de bevestiging.
  if (kanaal === eigen && (concept.kanaal === kanaal || kanaal === "telefoon")) {
    return { ...concept, tekst };
  }
  if (kanaal === "mail") {
    const adres = kanaalStand(zaak, "mail")?.adres ?? zaak.via?.aan ?? null;
    return {
      ...concept,
      kanaal: "mail",
      aan: concept.kanaal === "mail" && concept.aan.length ? concept.aan : adres ? [adres] : [],
      cc: concept.kanaal === "mail" ? concept.cc : [],
      bcc: concept.kanaal === "mail" ? concept.bcc : [],
      onderwerp:
        concept.kanaal === "mail" && concept.onderwerp
          ? concept.onderwerp
          : reOnderwerp(zaak.onderwerp),
      tekst,
    };
  }
  if (kanaal === "telefoon") {
    // Bellen: het tekstvak is de bevestiging per mail achteraf. Die
    // begint leeg; een chattekst hoort daar niet vanzelf in.
    return { ...concept, kanaal: "mail", aan: [], onderwerp: null, tekst: meeTekst ?? "" };
  }
  return { ...concept, kanaal, aan: [], cc: [], bcc: [], tekst };
}

/**
 * Het antwoordvak onderaan de pagina. Wordt per zaak opnieuw gemaakt
 * (key op de zaak), zodat alle lokale invoer leeg begint.
 */
export function Antwoordvak({ zaak, toonMelding }: { zaak: V6Zaak; toonMelding: ToonMelding }) {
  const { conceptVoorstel, leesConcept, startKanaal } = useV6();
  const metConcept = zaak.stand !== "live" && zaak.stand !== "afgerond";
  const conceptQuery = useQuery({
    queryKey: v6Sleutels.concept(zaak.zaak_id),
    queryFn: () => v6Api.concept(zaak.zaak_id),
    enabled: metConcept,
  });
  // Sessie 70 (punt 13): het kanaal dat JP koos (null = het beginkanaal),
  // en de tekst die bij een wissel meegaat naar de nieuwe stand.
  // Sessie 72 (punt 10): Nieuw bericht, Mail of WhatsApp bij de klant
  // openen het vak in dat kanaal (`?vak=`), ook als het uit staat: dan
  // zegt de stand waarom, en bij WhatsApp buiten het venster staan daar de
  // templates.
  const [gekozen, setGekozen] = useState<Kanaal | null>(() =>
    startKanaal && (zaak.kanalen ?? []).some((k) => k.kanaal === startKanaal) ? startKanaal : null,
  );
  const [meeTekst, setMeeTekst] = useState<string | null>(null);
  // De laatste antwoordtekst buiten Bellen: via Bellen wisselen (daar is
  // het vak de bevestiging) gooit het concept niet weg.
  const antwoordTekst = useRef<string | null>(null);
  const bewaard = useRef<VakBewaard>({ mail: null, bijlagen: 0 });
  const bewaar = useMemo<Bewaarder>(
    () => ({
      lees: () => bewaard.current,
      zet: (deel) => {
        bewaard.current = { ...bewaard.current, ...deel };
      },
    }),
    [],
  );
  // Een zin onder de keuze als een wissel iets niet kon of iets achterliet.
  const [regel, setRegel] = useState<string | null>(null);
  // Koos JP met het toetsenbord of de muis in de keuze, dan houdt de keuze
  // in de nieuwe stand de focus.
  const [keuzeFocus, setKeuzeFocus] = useState(false);
  const [voorstelGezien, setVoorstelGezien] = useState(0);
  const kanalen = zaak.kanalen ?? [];

  // "Zet dit klaar als mail": het voorstel van de copiloot noemt een
  // kanaal. Staat dat aan, dan wisselt het vak erheen; de nieuwe stand
  // neemt tekst en onderwerp zelf over uit het voorstel (useTekst). Staat
  // het uit, dan zegt het vak dat, in plaats van stil te blijven staan.
  if (
    metConcept &&
    conceptVoorstel &&
    conceptVoorstel.zaakId === zaak.zaak_id &&
    conceptVoorstel.volgnummer !== voorstelGezien
  ) {
    setVoorstelGezien(conceptVoorstel.volgnummer);
    const gevraagd = conceptVoorstel.kanaal;
    const stand = gevraagd ? kanalen.find((k) => k.kanaal === gevraagd) : undefined;
    if (gevraagd && stand?.aan) {
      setGekozen(gevraagd);
      setMeeTekst(null);
      setRegel(null);
    } else if (gevraagd && stand) {
      setRegel(
        `${KANAAL_WOORD[gevraagd]} staat uit (${stand.reden ?? "niet bereikbaar"}); de tekst staat in het open kanaal.`,
      );
    }
  }

  if (zaak.stand === "live") return <StandLive zaak={zaak} />;
  if (zaak.stand === "afgerond") return <StandAfgerond zaak={zaak} />;

  if (conceptQuery.isPending) {
    return (
      <div className="antwoord" aria-busy="true">
        <Kopregel icoon="antwoord" titel="Antwoord" />
        <p className="v6-leeg">Concept laden</p>
      </div>
    );
  }

  // Sessie 53 blok 1 (G2): een al verstuurd of afgerond concept komt
  // nooit in het vak; alle standen hieronder krijgen dan een leeg concept.
  const concept = vakConcept(conceptQuery.data ?? leegConcept(zaak));
  const geenGesprek =
    concept.kanaal === "geen" && (zaak.stand === "mail" || zaak.stand === "chat");

  const eigen = eigenKanaal(zaak, concept);
  const kanaal = gekozen ?? beginKanaal(zaak, concept);
  const kies = (nieuw: Kanaal) => {
    // De tekst die nu in het vak staat gaat mee (ook het bericht van
    // Beslissen); bij Bellen begint de bevestiging leeg, en terug uit
    // Bellen komt het antwoord van daarvoor weer.
    const vanBellen = gekozen !== null && kanaal === "telefoon";
    const huidig = vanBellen ? antwoordTekst.current : leesConcept();
    if (!vanBellen) antwoordTekst.current = huidig;
    setMeeTekst(nieuw === "telefoon" ? null : huidig);
    // Beslissen blijft staan (zelfde key), behalve naar en van Bellen.
    const blijftBeslis = zaak.stand === "beslis" && nieuw !== "telefoon" && kanaal !== "telefoon";
    setRegel(
      bewaard.current.bijlagen > 0 && !blijftBeslis
        ? "De bijlagen gingen niet mee naar het andere kanaal; voeg ze daar opnieuw toe."
        : null,
    );
    bewaar.zet({ bijlagen: 0 });
    setKeuzeFocus(Boolean(document.activeElement?.closest(".kanaalkeuze")));
    setGekozen(nieuw);
  };
  const keuze =
    kanalen.length > 0 ? (
      <>
        <KanaalKeuze
          kanalen={kanalen}
          gekozen={geenGesprek && gekozen === null ? null : kanaal}
          onKies={kies}
          focusBijStart={keuzeFocus}
        />
        {regel && (
          <p className="kanaalregel" role="status">
            {regel}
          </p>
        )}
      </>
    ) : null;

  if (geenGesprek && gekozen === null) {
    return (
      <StandGeenGesprek zaak={zaak} concept={concept} toonMelding={toonMelding} keuze={keuze} />
    );
  }

  const props: StandProps = {
    zaak,
    concept: conceptVoor(zaak, concept, kanaal, eigen, meeTekst),
    toonMelding,
    keuze,
    bewaar,
  };
  // Mail via het per-mail-pad als de zaak geen mailzaak is: de mail hangt
  // dan als eigen conversatie aan dezelfde zaak.
  const mailzaak = zaak.stand === "mail" || (zaak.stand === "beslis" && eigen === "mail");

  const stand = (() => {
    if (zaak.stand === "beslis" && kanaal !== "telefoon") {
      return <StandBeslis key="beslis" {...props} kanaal={kanaal} eigen={eigen} />;
    }
    switch (kanaal) {
      case "mail":
        return <StandMail key="mail" {...props} perMail={!mailzaak} />;
      case "telefoon":
        return <StandBel key="telefoon" {...props} />;
      case "whatsapp":
        // Sessie 9U (C18): nog geen gesprek, wel een nummer: eerst beginnen.
        if (kanaalStand(zaak, "whatsapp")?.starten)
          return <StandWhatsAppBeginnen key="whatsapp-beginnen" {...props} />;
        return <StandChat key={kanaal} {...props} kanaal={kanaal} />;
      default:
        return <StandChat key={kanaal} {...props} kanaal={kanaal} />;
    }
  })();

  return (
    <>
      {conceptQuery.isError && (
        <p className="v6-fout" role="alert">
          Het concept kon niet geladen worden. Typ zelf of probeer het zo opnieuw.
        </p>
      )}
      {stand}
    </>
  );
}
